import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, ButtonGroup, Divider } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { HeroMetric, InformationBanner } from 'src/components/molecules';
import { ModalSize } from 'src/components/molecules/Modal/Modal.styles';
import { PaginatedTable } from 'src/components/organisms';
import { EvidenceItem } from 'src/hooks/cases/useUploadCases';
import { FeatureFlag, FormState, State } from 'src/types';
import {
  trackCaseImportCancelled,
  trackCaseImportConfirmed,
  trackCaseImportReviewOpened,
} from 'src/utils/analytics/track/caseImport';
import { Modal } from '../../../molecules/Modal';
import { UploadEvidence } from '../components/UploadEvidence';
import { StepContainer, TableContainer } from '../UploadCases.styles';
import {
  BannersContainer,
  Bottom,
  MetricsContainer,
  MetricsItem,
  StyledText,
  StyledTextBold,
  SummaryContainer,
  TableSectionWrapper,
  TableTopContainer,
  Title,
} from './CaseReview.styles';

export interface TableData {
  cellData: (React.ReactNode | string | string[] | null | undefined)[];
  row: string;
  data: {};
}

export interface CaseReviewProps {
  tableColumns: string[];
  tableData: TableData[];
  duplicateTableColumns: string[];
  duplicateTableData: TableData[];
  isRoutingEnabled: boolean;
  importedCaseIds: string[];
  commit: (onError?: () => void, onSuccess?: () => void) => void;
  setUploadEvidences: React.Dispatch<React.SetStateAction<EvidenceItem[]>>;
  uploadEvidences: EvidenceItem[];
  importFileName: string;
  totalCasesInCSV: number;
  stateProperties: {
    organisationId: string | null;
    userId: number | null;
    userSystemRole: string | null;
  };
}

const MIN_TABLE_HEIGHT: number = 300;
const MAX_ROWS_PER_PAGE: number = 10;
const MAX_COLUMN_WIDTH: number = 200;

const enum tabOptions {
  READY_TO_IMPORT = 'Ready to import',
  DUPLICATES = 'Duplicates',
}

type ModalConfig = {
  size: ModalSize;
  title: string;
  confirmButtonText: 'Confirm Import';
  open: boolean;
  loading: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCancel: () => void;
  onConfirm: () => void;
};

type ModalProps = {
  totalDuplicates: number;
  totalCases: number;
  confirmModalOpen: boolean;
  commit: (onError?: () => void, onSuccess?: () => void) => void;
  setConfirmModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

type RenderModalProps = ModalProps & {
  segmentEnabled: boolean;
  stateProperties: {
    organisationId: string | null;
    userId: number | null;
  };
  importFileName: string;
  totalRows: number;
  rowsReadyToImport: number;
  rowsDroppedDuplicates: number;
};

const RenderModal: (props: RenderModalProps) => React.JSX.Element = ({
  totalDuplicates,
  totalCases,
  confirmModalOpen,
  commit,
  setConfirmModalOpen,
  segmentEnabled,
  stateProperties,
  importFileName,
  totalRows,
  rowsReadyToImport,
  rowsDroppedDuplicates,
}) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = () => {
    setLoading(true);
    if (segmentEnabled && stateProperties?.organisationId) {
      trackCaseImportConfirmed({
        organisation_id: Number(stateProperties.organisationId),
        user_id: String(stateProperties.userId),
        file_name: importFileName,
        total_rows: totalRows,
        rows_ready_to_import: rowsReadyToImport,
        rows_dropped_duplicates: rowsDroppedDuplicates,
      });
    }
    setConfirmModalOpen(false);
    commit();
  };

  const handleCancel = () => {
    if (segmentEnabled && stateProperties?.organisationId) {
      trackCaseImportCancelled({
        organisation_id: Number(stateProperties.organisationId),
        user_id: String(stateProperties.userId),
        file_name: importFileName,
        total_rows: totalRows,
        rows_ready_to_import: rowsReadyToImport,
        rows_dropped_duplicates: rowsDroppedDuplicates,
      });
    }
    setConfirmModalOpen(false);
  };

  const config: ModalConfig = {
    title: 'Confirm Import',
    confirmButtonText: 'Confirm Import',
    setOpen: setConfirmModalOpen,
    onCancel: handleCancel,
    onConfirm: handleConfirm,
    open: confirmModalOpen,
    size: 'small',
    loading: loading,
  };

  return (
    <Modal {...config} data-testid="case-import-confirm-modal">
      <StyledText>
        You are about to import <b>{totalCases} case(s)</b> to the system.{' '}
        {totalDuplicates} duplicate case(s) will be automatically excluded.
      </StyledText>
      <StyledText>
        This action is irreversible. Once you import these cases, they cannot be
        automatically removed from the system. If you need to remove them later,
        you will have to do so manually.
      </StyledText>
    </Modal>
  );
};

export const CaseReview = ({
  tableColumns,
  tableData,
  duplicateTableColumns,
  duplicateTableData,
  isRoutingEnabled,
  importedCaseIds,
  setUploadEvidences,
  uploadEvidences,
  commit,
  importFileName,
  totalCasesInCSV,
  stateProperties,
}: CaseReviewProps) => {
  const [selectedTab, setSelectedTab] = useState(tabOptions.READY_TO_IMPORT);
  const [isUploading, setIsUploading] = useState(false);
  const [numPages, setNumPages] = useState(1);
  const [page, setPage] = useState(1);
  const [numPagesDup, setNumPagesDup] = useState(1);
  const [pageDup, setPageDup] = useState(1);
  const [showDuplicateBanner, setShowDuplicateBanner] = useState(true);
  const [showRoutingBanner, setShowRoutingBanner] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasTrackedReviewOpened, setHasTrackedReviewOpened] = useState(false);

  const features = useSelector((state: State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  useEffect(() => {
    if (tableData.length > MAX_ROWS_PER_PAGE) {
      setNumPages(Math.ceil(tableData.length / MAX_ROWS_PER_PAGE));
    }
    if (duplicateTableData.length > MAX_ROWS_PER_PAGE) {
      setNumPagesDup(Math.ceil(duplicateTableData.length / MAX_ROWS_PER_PAGE));
    }
  }, [tableData.length, duplicateTableData.length]);

  useEffect(() => {
    if (
      !hasTrackedReviewOpened &&
      segmentEnabled &&
      stateProperties?.organisationId
    ) {
      setHasTrackedReviewOpened(true);
      trackCaseImportReviewOpened({
        organisation_id: Number(stateProperties.organisationId),
        user_id: String(stateProperties.userId),
        file_name: importFileName,
        total_rows: totalCasesInCSV,
        rows_ready_to_import: tableData.length,
        rows_dropped_duplicates: duplicateTableData.length,
      });
    }
  }, [
    hasTrackedReviewOpened,
    segmentEnabled,
    stateProperties?.organisationId,
    stateProperties?.userId,
    importFileName,
    totalCasesInCSV,
    tableData.length,
    duplicateTableData.length,
  ]);

  const updateTabSelected = (option: tabOptions) => {
    setSelectedTab(option);
    setPage(1);
    setPageDup(1);
  };

  const hasDuplicates: boolean = duplicateTableData.length > 0;
  const metrics = [
    {
      id: 'ready-to-import-cases-metric',
      title: 'Ready to Import',
      metrics: [{ value: tableData.length, title: 'New cases' }],
      icon: 'uil uil-check-circle',
    },
    {
      id: 'duplicates-import-cases-metric',
      title: 'Duplicates Found',
      metrics: [
        {
          value: duplicateTableData.length,
          title: 'Excluded from import',
        },
      ],
      icon: 'uil uil-exclamation-triangle',
    },
    {
      id: 'total-file-import-cases-metric',
      title: 'Total in File',
      metrics: [
        {
          value: tableData.length + duplicateTableData.length,
          title: 'Total cases in the import file',
        },
      ],
      icon: 'uil uil-info-circle',
    },
  ];

  const autoRoutingWarning = `If the case lead is missing or doesn't match any case manager in the active directory, it will be routed automatically${
    isRoutingEnabled ? ' according to your predefined rules.' : '.'
  }`;

  const confirmButtonText =
    tableData.length === 0
      ? 'No cases to import'
      : `Import ${tableData.length} cases`;

  return (
    <StepContainer>
      <Title data-testid="case-import-review-title">Review Cases</Title>
      <MetricsContainer>
        {metrics.map((metric) => (
          <MetricsItem key={metric.id}>
            <HeroMetric
              icon={metric.icon}
              data-testid={metric.id}
              key={metric.id}
              title={metric.title}
              metrics={metric.metrics}
              dashboard="organisational"
            />
          </MetricsItem>
        ))}
      </MetricsContainer>
      <RenderModal
        totalDuplicates={duplicateTableData.length}
        totalCases={tableData.length}
        commit={commit}
        confirmModalOpen={confirmModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
        segmentEnabled={segmentEnabled}
        stateProperties={stateProperties}
        importFileName={importFileName}
        totalRows={totalCasesInCSV}
        rowsReadyToImport={tableData.length}
        rowsDroppedDuplicates={duplicateTableData.length}
      />
      <BannersContainer>
        {hasDuplicates && showDuplicateBanner && (
          <InformationBanner
            data-testid="duplicate-cases-warning-banner"
            $fullWidth
            state={FormState.WARNING}
            onCloseMessage={() => setShowDuplicateBanner(false)}
            title="Duplicate cases will not be imported"
            description={`${duplicateTableData.length} case(s) match existing records in the system based on their Imported Case ID. To import them as new cases, reinitiate the upload without an Imported Case ID.`}
          />
        )}
        {showRoutingBanner && (
          <InformationBanner
            data-testid="automatic-routing-warning-banner"
            $fullWidth
            state={FormState.WARNING}
            onCloseMessage={() => setShowRoutingBanner(false)}
            title="Automatic Routing"
            description={autoRoutingWarning}
          />
        )}
      </BannersContainer>
      <TableSectionWrapper>
        <TableTopContainer>
          {duplicateTableData.length > 0 && (
            <ButtonGroup
              buttonGroupOptionTestId="case-import-tabs"
              options={[tabOptions.READY_TO_IMPORT, tabOptions.DUPLICATES]}
              setSelectedOption={updateTabSelected}
              size="small"
              selectedOption={selectedTab}
            />
          )}
          <UploadEvidence
            importedCaseIds={importedCaseIds}
            uploadEvidences={uploadEvidences}
            setUploadEvidences={setUploadEvidences}
            onUploadingChange={setIsUploading}
          />
        </TableTopContainer>
        {selectedTab === tabOptions.READY_TO_IMPORT && (
          <TableContainer>
            <PaginatedTable
              data-testid="case-import-review-table"
              minHeight={MIN_TABLE_HEIGHT}
              tableData={tableData}
              columns={tableColumns.map((column) => {
                return {
                  label: column,
                  value: column,
                  showOverflow: false,
                };
              })}
              isScrollable={true}
              fixedRowHeight={true}
              isFullHeight={false}
              showPagination
              borderRadius="12px"
              paginationPadding="24px 0px"
              visibleColumns={tableColumns.map((column) => {
                return {
                  label: column,
                  value: column,
                  showOverflow: false,
                };
              })}
              emptyText="No cases to import."
              maxRows={MAX_ROWS_PER_PAGE}
              numPages={numPages}
              handleClickPage={(index: number) => setPage(index)}
              currentPage={page}
              maxColumnWidth={`${MAX_COLUMN_WIDTH}px`}
            />
          </TableContainer>
        )}
        {selectedTab === tabOptions.DUPLICATES && (
          <TableContainer>
            <PaginatedTable
              data-testid="case-import-review-duplicates-table"
              minHeight={MIN_TABLE_HEIGHT}
              isFullWidth={true}
              tableData={duplicateTableData}
              columns={duplicateTableColumns.map((column) => {
                return {
                  label: column,
                  value: column,
                  showOverflow: false,
                };
              })}
              isScrollable={true}
              fixedRowHeight={true}
              isFullHeight={false}
              maxColumnWidth={`${MAX_COLUMN_WIDTH}px`}
              showPagination
              borderRadius="12px"
              paginationPadding="24px 0px"
              visibleColumns={duplicateTableColumns.map((column) => {
                return {
                  label: column,
                  value: column,
                  showOverflow: false,
                };
              })}
              emptyText="No duplicate cases."
              maxRows={MAX_ROWS_PER_PAGE}
              numPages={numPagesDup}
              handleClickPage={(index: number) => setPageDup(index)}
              currentPage={pageDup}
            />
          </TableContainer>
        )}
      </TableSectionWrapper>
      <Divider variant="horizontal" />
      <Bottom>
        <SummaryContainer>
          <StyledTextBold data-testid={'total-cases-text'}>
            {`${tableData.length} case(s) ready to import ${
              hasDuplicates ? '|' : ''
            }`}
          </StyledTextBold>
          {hasDuplicates && (
            <StyledText
              data-testid={'total-duplicate-cases-text'}
              style={{ paddingLeft: 5 }}
            >{`${duplicateTableData.length} duplicate(s) excluded`}</StyledText>
          )}
        </SummaryContainer>
        <Button
          disabled={confirmModalOpen || tableData.length === 0 || isUploading}
          onClick={() => setConfirmModalOpen(true)}
        >
          {confirmButtonText}
        </Button>
      </Bottom>
    </StepContainer>
  );
};
